import { TEMPLATES } from "@/data/civilTemplates";
// Free interactive Civil Hub templates — ITP, Variation, Progress Claim.
// Config-driven; print via browser (Fieldlink-branded printable area).
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Printer, Plus, Trash2, ArrowRight } from "lucide-react";
import { SEO } from "@/lib/seo";
import { PublicFooter } from "../FAQ";
import { MarketingHeader, Breadcrumb, breadcrumbJsonLd } from "./SeoProductPage";
import { hubTrack } from "./Calculators";

const r2 = (n) => Math.round(n * 100) / 100;
const money = (n) => `$${(Number.isFinite(n) ? r2(n) : 0).toLocaleString("en-NZ", { minimumFractionDigits: 2 })}`;

export { TEMPLATES };

const inputCls = "w-full rounded-md border border-[#cbd3d5] bg-white text-[#142635] placeholder:text-[#52616c] focus:border-[#FF5F15] outline-none px-2.5 py-2 text-sm";
const Field = ({ label, value, onChange, type = "text", placeholder, testId, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="text-[10px] font-bold uppercase tracking-wider text-[#52616c]">{label}</span>
    <input type={type} inputMode={type === "number" ? "decimal" : undefined} value={value} placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)} className={`mt-1 ${inputCls}`} data-testid={testId} />
  </label>
);
const Area = ({ label, value, onChange, placeholder, testId, rows = 3 }) => (
  <label className="block">
    <span className="text-[10px] font-bold uppercase tracking-wider text-[#52616c]">{label}</span>
    <textarea rows={rows} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)}
      className={`mt-1 ${inputCls} resize-y`} data-testid={testId} />
  </label>
);
const SignBlocks = ({ labels }) => (
  <div className={`mt-6 grid gap-3 ${labels.length >= 3 ? "sm:grid-cols-3" : "sm:grid-cols-2"}`}>
    {labels.map((s) => (
      <div key={s} className="rounded-lg border border-[#cbd3d5] bg-white p-4">
        <div className="text-[10px] font-bold uppercase tracking-wider text-[#52616c]">{s}</div>
        <div className="mt-6 border-b border-[#cbd3d5]" />
        <div className="mt-1 text-[10px] text-[#52616c]">Name / signature / date</div>
      </div>
    ))}
  </div>
);
// Generic editable rows table (hazards, attendees, checks...)
function Rows({ cols, rows, setRows, addLabel, testPrefix }) {
  const setCell = (i, k, v) => setRows((p) => p.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <>
      <div className="mt-4 overflow-x-auto rounded-lg border border-[#cbd3d5]">
        <table className="w-full text-sm min-w-[560px]">
          <thead className="bg-white">
            <tr>{cols.map(([, h]) => <th key={h} className="text-left px-3 py-2.5 text-[10px] font-mono uppercase tracking-widest text-[#a63c0b]">{h}</th>)}<th className="print:hidden w-10" /></tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-[#cbd3d5]">
                {cols.map(([k]) => (
                  <td key={k} className="px-2 py-1.5 align-top">
                    <input value={r[k]} onChange={(e) => setCell(i, k, e.target.value)} className={inputCls} data-testid={`${testPrefix}-row-${i}-${k}`} />
                  </td>
                ))}
                <td className="px-2 print:hidden">
                  <button onClick={() => setRows((p) => p.filter((_, j) => j !== i))} className="text-[#52616c] hover:text-red-400" aria-label="Remove row"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button onClick={() => setRows((p) => [...p, Object.fromEntries(cols.map(([k]) => [k, ""]))])}
        className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-[#cbd3d5] text-[#465562] hover:border-[#FF5F15] hover:text-[#a63c0b] px-3.5 py-2 text-[11px] font-bold uppercase tracking-wider print:hidden" data-testid={`${testPrefix}-add-row`}>
        <Plus className="w-3.5 h-3.5" /> {addLabel}
      </button>
    </>
  );
}

/* ------------------------------- ITP ------------------------------- */
const ITP_COLS = [["activity", "Activity"], ["inspection", "Inspection / test"], ["criteria", "Acceptance criteria"], ["point", "Hold / witness"], ["responsible", "Responsible"], ["evidence", "Evidence required"]];
function ItpBody() {
  const [head, setHead] = useState({ project: "", location: "", contractor: "", date: "" });
  const [rows, setRows] = useState([
    { activity: "Trench excavation", inspection: "Depth & grade check", criteria: "As per approved drawings ±10mm", point: "Witness", responsible: "Foreman", evidence: "Survey record" },
    { activity: "Pipe laying", inspection: "Bedding, jointing & grade", criteria: "AS/NZS spec + drawings", point: "Hold", responsible: "Engineer", evidence: "Photos + as-built" },
  ]);
  const setH = (k) => (v) => setHead((p) => ({ ...p, [k]: v }));
  const setCell = (i, k, v) => setRows((p) => p.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <div data-testid="tpl-itp">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Field label="Project" value={head.project} onChange={setH("project")} placeholder="Rata St Stage 2" testId="itp-project" />
        <Field label="Location" value={head.location} onChange={setH("location")} placeholder="Auckland" testId="itp-location" />
        <Field label="Contractor" value={head.contractor} onChange={setH("contractor")} placeholder="Your company" testId="itp-contractor" />
        <Field label="Date" value={head.date} onChange={setH("date")} type="date" testId="itp-date" />
      </div>
      <div className="mt-5 overflow-x-auto rounded-lg border border-[#cbd3d5]">
        <table className="w-full text-sm min-w-[900px]">
          <thead className="bg-white">
            <tr>{ITP_COLS.map(([, h]) => <th key={h} className="text-left px-3 py-2.5 text-[10px] font-mono uppercase tracking-widest text-[#a63c0b]">{h}</th>)}<th className="print:hidden w-10" /></tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-[#cbd3d5]">
                {ITP_COLS.map(([k]) => (
                  <td key={k} className="px-2 py-1.5 align-top">
                    <input value={r[k]} onChange={(e) => setCell(i, k, e.target.value)} className={inputCls} data-testid={`itp-row-${i}-${k}`} />
                  </td>
                ))}
                <td className="px-2 print:hidden">
                  <button onClick={() => setRows((p) => p.filter((_, j) => j !== i))} className="text-[#52616c] hover:text-red-400" aria-label="Remove row" data-testid={`itp-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button onClick={() => setRows((p) => [...p, Object.fromEntries(ITP_COLS.map(([k]) => [k, ""]))])}
        className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-[#cbd3d5] text-[#465562] hover:border-[#FF5F15] hover:text-[#a63c0b] px-3.5 py-2 text-[11px] font-bold uppercase tracking-wider print:hidden" data-testid="itp-add-row">
        <Plus className="w-3.5 h-3.5" /> Add activity
      </button>
      <div className="mt-6 grid sm:grid-cols-3 gap-3">
        {["Prepared by", "Reviewed by", "Approved by"].map((s) => (
          <div key={s} className="rounded-lg border border-[#cbd3d5] bg-white p-4">
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#52616c]">{s}</div>
            <div className="mt-6 border-b border-[#cbd3d5]" />
            <div className="mt-1 text-[10px] text-[#52616c]">Name / signature / date</div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------------------- VARIATION ---------------------------- */
const VAR_CATS = ["Labour", "Plant", "Materials", "Subcontractor", "Other"];
function VariationBody() {
  const [head, setHead] = useState({ contractor: "", client: "", project: "", ref: "", date: "", description: "", reason: "", status: "Pending" });
  const [lines, setLines] = useState(VAR_CATS.map((c) => ({ cat: c, cost: "", markup: c === "Subcontractor" ? "10" : "15" })));
  const [gst, setGst] = useState(true);
  const setH = (k) => (v) => setHead((p) => ({ ...p, [k]: v }));
  const setL = (i, k, v) => setLines((p) => p.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const totals = useMemo(() => {
    const rows = lines.map((l) => {
      const cost = Number(l.cost) || 0;
      const mk = cost * ((Number(l.markup) || 0) / 100);
      return { ...l, costN: cost, mk, sell: cost + mk };
    });
    const cost = rows.reduce((a, r) => a + r.costN, 0);
    const mk = rows.reduce((a, r) => a + r.mk, 0);
    const sub = cost + mk;
    const gstAmt = gst ? sub * 0.15 : 0;
    return { rows, cost, mk, sub, gstAmt, total: sub + gstAmt };
  }, [lines, gst]);
  return (
    <div data-testid="tpl-variation">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Field label="Contractor" value={head.contractor} onChange={setH("contractor")} placeholder="Your company" testId="var-contractor" />
        <Field label="Client / principal" value={head.client} onChange={setH("client")} placeholder="Client name" testId="var-client" />
        <Field label="Project / contract" value={head.project} onChange={setH("project")} placeholder="Mill Rd Drainage" testId="var-project" />
        <Field label="Variation ref" value={head.ref} onChange={setH("ref")} placeholder="VO-004" testId="var-ref" />
        <Field label="Date" value={head.date} onChange={setH("date")} type="date" testId="var-date" />
        <Field label="Description of change" value={head.description} onChange={setH("description")} placeholder="Additional 24m SW line incl. reinstatement" testId="var-description" className="lg:col-span-2" />
        <Field label="Reason / instruction source" value={head.reason} onChange={setH("reason")} placeholder="Engineer's instruction EI-12" testId="var-reason" />
      </div>
      <div className="mt-3 flex items-center gap-2 print:hidden">
        <span className="text-[10px] font-bold uppercase tracking-wider text-[#52616c]">Status</span>
        {["Pending", "Submitted", "Approved", "Declined"].map((s) => (
          <button key={s} onClick={() => setH("status")(s)}
            className={`text-[10px] font-bold uppercase tracking-wider rounded-md px-2.5 py-1.5 border transition-colors ${head.status === s ? "border-[#FF5F15] text-[#a63c0b] bg-[#FF5F15]/10" : "border-[#cbd3d5] text-[#52616c] hover:text-[#142635]"}`}
            data-testid={`var-status-${s.toLowerCase()}`}>{s}</button>
        ))}
      </div>
      <div className="mt-5 overflow-x-auto rounded-lg border border-[#cbd3d5]">
        <table className="w-full text-sm min-w-[620px]">
          <thead className="bg-white">
            <tr>{["Category", "Direct cost ($)", "Markup (%)", "Markup ($)", "Sell ($)"].map((h) => <th key={h} className="text-left px-3 py-2.5 text-[10px] font-mono uppercase tracking-widest text-[#a63c0b]">{h}</th>)}</tr>
          </thead>
          <tbody>
            {totals.rows.map((l, i) => (
              <tr key={l.cat} className="border-t border-[#cbd3d5]">
                <td className="px-3 py-2 font-bold text-[#344451]">{l.cat}</td>
                <td className="px-2 py-1.5 w-36"><input type="number" inputMode="decimal" value={l.cost} onChange={(e) => setL(i, "cost", e.target.value)} className={inputCls} data-testid={`var-cost-${l.cat.toLowerCase()}`} /></td>
                <td className="px-2 py-1.5 w-28"><input type="number" inputMode="decimal" value={l.markup} onChange={(e) => setL(i, "markup", e.target.value)} className={inputCls} data-testid={`var-markup-${l.cat.toLowerCase()}`} /></td>
                <td className="px-3 py-2 font-mono text-[#52616c]">{money(l.mk)}</td>
                <td className="px-3 py-2 font-mono text-[#142635]">{money(l.sell)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <label className="mt-4 flex items-center gap-2 text-[13px] font-semibold text-[#465562] print:hidden">
        <input type="checkbox" checked={gst} onChange={() => setGst((g) => !g)} className="w-4 h-4 accent-[#FF5F15]" data-testid="var-gst" /> Add GST (15%)
      </label>
      <div className="mt-4 rounded-lg bg-white border border-[#cbd3d5] p-5 max-w-md ml-auto" data-testid="var-totals">
        {[["Direct cost", totals.cost], ["Markup", totals.mk], ["Subtotal", totals.sub], ...(gst ? [["GST (15%)", totals.gstAmt]] : [])].map(([l, v]) => (
          <div key={l} className="flex justify-between text-sm py-1"><span className="text-[#52616c]">{l}</span><span className="font-mono text-[#142635]">{money(v)}</span></div>
        ))}
        <div className="flex justify-between pt-2 mt-1 border-t border-[#cbd3d5]">
          <span className="text-[11px] font-black uppercase tracking-wider text-[#465562]">Variation total</span>
          <span className="font-display font-black text-xl text-[#a63c0b]" data-testid="var-total">{money(totals.total)}</span>
        </div>
      </div>
    </div>
  );
}

/* -------------------------- PROGRESS CLAIM -------------------------- */
function ClaimBody() {
  const [f, setF] = useState({ project: "", claimNo: "", contract: "500000", variations: "18000", done: "62", previous: "230000", retentionPct: "5", retentionCap: "" });
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const c = useMemo(() => {
    const contract = Number(f.contract) || 0, vars = Number(f.variations) || 0;
    const done = Math.min(Math.max(Number(f.done) || 0, 0), 100);
    const prev = Number(f.previous) || 0;
    const gross = (contract + vars) * (done / 100);
    let retention = gross * ((Number(f.retentionPct) || 0) / 100);
    const cap = Number(f.retentionCap) || 0;
    if (cap > 0) retention = Math.min(retention, cap);
    const claim = Math.max(gross - retention - prev, 0);
    const gst = claim * 0.15;
    return { gross, retention, prev, claim, gst, due: claim + gst };
  }, [f]);
  return (
    <div data-testid="tpl-claim">
      <div className="grid md:grid-cols-2 gap-6">
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label="Project" value={f.project} onChange={set("project")} placeholder="Harbour View Subdivision" testId="claim-project" />
            <Field label="Claim number" value={f.claimNo} onChange={set("claimNo")} placeholder="PC-06" testId="claim-no" />
          </div>
          <Field label="Contract value (excl. GST)" value={f.contract} onChange={set("contract")} type="number" testId="claim-contract" />
          <Field label="Approved variations (excl. GST)" value={f.variations} onChange={set("variations")} type="number" testId="claim-variations" />
          <Field label="Work completed to date (%)" value={f.done} onChange={set("done")} type="number" testId="claim-done" />
          <Field label="Previously claimed (excl. GST, net of retention)" value={f.previous} onChange={set("previous")} type="number" testId="claim-previous" />
          <div className="grid grid-cols-2 gap-3">
            <Field label="Retention (%)" value={f.retentionPct} onChange={set("retentionPct")} type="number" testId="claim-retention" />
            <Field label="Retention cap ($, optional)" value={f.retentionCap} onChange={set("retentionCap")} type="number" testId="claim-cap" />
          </div>
        </div>
        <div className="rounded-lg bg-white border border-[#cbd3d5] p-6" data-testid="claim-results">
          <p className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#a63c0b] mb-4">This claim</p>
          {[["Gross value of work to date", c.gross], ["Less retention withheld", -c.retention], ["Less previously claimed", -c.prev]].map(([l, v]) => (
            <div key={l} className="flex justify-between text-sm py-1.5 border-b border-[#cbd3d5]"><span className="text-[#52616c]">{l}</span><span className="font-mono text-[#142635]">{money(v)}</span></div>
          ))}
          <div className="flex justify-between text-sm py-1.5 border-b border-[#cbd3d5]"><span className="text-[#465562] font-bold">Current claim (excl. GST)</span><span className="font-mono font-bold text-[#142635]" data-testid="claim-current">{money(c.claim)}</span></div>
          <div className="flex justify-between text-sm py-1.5 border-b border-[#cbd3d5]"><span className="text-[#52616c]">GST (15%)</span><span className="font-mono text-[#142635]">{money(c.gst)}</span></div>
          <div className="flex justify-between pt-3">
            <span className="text-[11px] font-black uppercase tracking-wider text-[#465562]">Amount due</span>
            <span className="font-display font-black text-2xl text-[#a63c0b]" data-testid="claim-due">{money(c.due)}</span>
          </div>
          <p className="text-[10.5px] text-[#52616c] mt-4">Assumes claims are cumulative and retention applies to gross work to date. Check your contract's payment and retention terms — this is not legal or contractual advice.</p>
        </div>
      </div>
    </div>
  );
}

/* --------------------------- FIELD TEMPLATES --------------------------- */
function PreStartBody() {
  const [f, setF] = useState({ project: "", date: "", crew: "", plant: "", weather: "", work: "", ppe: "Hi-vis, boots, hard hat, gloves, eye protection", changes: "" });
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const [haz, setHaz] = useState([{ hazard: "Underground services", control: "Service plans reviewed, pothole before dig" }, { hazard: "Plant / people interface", control: "Exclusion zones, spotter, positive comms" }]);
  return (
    <div data-testid="tpl-prestart">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Field label="Project" value={f.project} onChange={set("project")} testId="ps-project" />
        <Field label="Date" value={f.date} onChange={set("date")} type="date" testId="ps-date" />
        <Field label="Crew" value={f.crew} onChange={set("crew")} placeholder="Names" testId="ps-crew" />
        <Field label="Weather" value={f.weather} onChange={set("weather")} testId="ps-weather" />
      </div>
      <div className="grid sm:grid-cols-2 gap-3 mt-3">
        <Field label="Plant on site" value={f.plant} onChange={set("plant")} placeholder="14t digger, roller, truck" testId="ps-plant" />
        <Field label="PPE required" value={f.ppe} onChange={set("ppe")} testId="ps-ppe" />
      </div>
      <div className="mt-3"><Area label="Today's work" value={f.work} onChange={set("work")} placeholder="What the crew is doing today" testId="ps-work" /></div>
      <Rows cols={[["hazard", "Hazard"], ["control", "Control"]]} rows={haz} setRows={setHaz} addLabel="Add hazard" testPrefix="ps" />
      <div className="mt-3"><Area label="Changes since yesterday" value={f.changes} onChange={set("changes")} rows={2} testId="ps-changes" /></div>
      <SignBlocks labels={["Supervisor sign-off", "Crew sign-on"]} />
    </div>
  );
}

function SiteDiaryBody() {
  const [f, setF] = useState({ job: "", date: "", weather: "", crew: "", plant: "", work: "", deliveries: "", visitors: "", delays: "", issues: "", notes: "" });
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  return (
    <div data-testid="tpl-diary">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Field label="Job" value={f.job} onChange={set("job")} testId="sd-job" />
        <Field label="Date" value={f.date} onChange={set("date")} type="date" testId="sd-date" />
        <Field label="Weather" value={f.weather} onChange={set("weather")} testId="sd-weather" />
        <Field label="Crew on site" value={f.crew} onChange={set("crew")} testId="sd-crew" />
      </div>
      <div className="mt-3"><Field label="Plant on site" value={f.plant} onChange={set("plant")} testId="sd-plant" /></div>
      <div className="grid sm:grid-cols-2 gap-3 mt-3">
        <Area label="Work completed" value={f.work} onChange={set("work")} testId="sd-work" />
        <Area label="Deliveries" value={f.deliveries} onChange={set("deliveries")} testId="sd-deliveries" />
        <Area label="Visitors" value={f.visitors} onChange={set("visitors")} rows={2} testId="sd-visitors" />
        <Area label="Delays (cause + duration)" value={f.delays} onChange={set("delays")} rows={2} testId="sd-delays" />
        <Area label="Issues / instructions received" value={f.issues} onChange={set("issues")} rows={2} testId="sd-issues" />
        <Area label="Notes" value={f.notes} onChange={set("notes")} rows={2} testId="sd-notes" />
      </div>
      <SignBlocks labels={["Completed by", "Reviewed by"]} />
    </div>
  );
}

function ToolboxBody() {
  const [f, setF] = useState({ topic: "", site: "", date: "", presenter: "", points: "", questions: "" });
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const [haz, setHaz] = useState([{ hazard: "", control: "" }]);
  const [att, setAtt] = useState([{ name: "", signature: "" }, { name: "", signature: "" }, { name: "", signature: "" }]);
  return (
    <div data-testid="tpl-toolbox">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Field label="Topic" value={f.topic} onChange={set("topic")} placeholder="Working around live services" testId="tb-topic" />
        <Field label="Site" value={f.site} onChange={set("site")} testId="tb-site" />
        <Field label="Date" value={f.date} onChange={set("date")} type="date" testId="tb-date" />
        <Field label="Presented by" value={f.presenter} onChange={set("presenter")} testId="tb-presenter" />
      </div>
      <div className="mt-3"><Area label="Discussion points" value={f.points} onChange={set("points")} testId="tb-points" /></div>
      <Rows cols={[["hazard", "Hazard discussed"], ["control", "Control"]]} rows={haz} setRows={setHaz} addLabel="Add hazard" testPrefix="tb" />
      <div className="mt-3"><Area label="Questions raised" value={f.questions} onChange={set("questions")} rows={2} testId="tb-questions" /></div>
      <p className="mt-5 text-[10px] font-bold uppercase tracking-widest text-[#52616c]">Attendees</p>
      <Rows cols={[["name", "Name"], ["signature", "Signature"]]} rows={att} setRows={setAtt} addLabel="Add attendee" testPrefix="tb-att" />
    </div>
  );
}

const PLANT_CHECKS = ["Walk-around — leaks, damage, tyres/tracks", "Fluids — oil, coolant, hydraulic", "Attachments & pins secure", "Lights, beacon, horn, reverse alarm", "Seatbelt & ROPS", "Mirrors / cameras clean", "Fire extinguisher & first aid", "Bucket / GET condition"];
function PlantInspectionBody() {
  const [f, setF] = useState({ plant: "", operator: "", date: "", hours: "", defects: "" });
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const [checks, setChecks] = useState(PLANT_CHECKS.map((c) => ({ item: c, result: "OK", notes: "" })));
  const [status, setStatus] = useState("SAFE");
  const setCheck = (i, k, v) => setChecks((p) => p.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <div data-testid="tpl-plant">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Field label="Plant / machine" value={f.plant} onChange={set("plant")} placeholder="CAT 313 — FL-07" testId="pi-plant" />
        <Field label="Operator" value={f.operator} onChange={set("operator")} testId="pi-operator" />
        <Field label="Date" value={f.date} onChange={set("date")} type="date" testId="pi-date" />
        <Field label="Hour reading" value={f.hours} onChange={set("hours")} type="number" testId="pi-hours" />
      </div>
      <div className="mt-4 overflow-x-auto rounded-lg border border-[#cbd3d5]">
        <table className="w-full text-sm min-w-[560px]">
          <thead className="bg-white"><tr>{["Check", "Result", "Notes"].map((h) => <th key={h} className="text-left px-3 py-2.5 text-[10px] font-mono uppercase tracking-widest text-[#a63c0b]">{h}</th>)}</tr></thead>
          <tbody>
            {checks.map((c, i) => (
              <tr key={c.item} className="border-t border-[#cbd3d5]">
                <td className="px-3 py-2 text-[#344451] text-[13px]">{c.item}</td>
                <td className="px-2 py-1.5 w-32">
                  <select value={c.result} onChange={(e) => setCheck(i, "result", e.target.value)} className={`${inputCls} bg-white`} data-testid={`pi-result-${i}`}>
                    {["OK", "Defect", "N/A"].map((o) => <option key={o}>{o}</option>)}
                  </select>
                </td>
                <td className="px-2 py-1.5"><input value={c.notes} onChange={(e) => setCheck(i, "notes", e.target.value)} className={inputCls} data-testid={`pi-notes-${i}`} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-3"><Area label="Damage / defects detail" value={f.defects} onChange={set("defects")} rows={2} testId="pi-defects" /></div>
      <div className="mt-4 flex items-center gap-2">
        <span className="text-[10px] font-bold uppercase tracking-wider text-[#52616c]">Status</span>
        {["SAFE", "UNSAFE — DO NOT OPERATE"].map((s) => (
          <button key={s} onClick={() => setStatus(s)}
            className={`text-[10px] font-bold uppercase tracking-wider rounded-md px-3 py-1.5 border transition-colors ${status === s ? (s === "SAFE" ? "border-emerald-500 text-emerald-400 bg-emerald-500/10" : "border-red-500 text-red-400 bg-red-500/10") : "border-[#cbd3d5] text-[#52616c] hover:text-[#142635]"}`}
            data-testid={`pi-status-${s === "SAFE" ? "safe" : "unsafe"}`}>{s}</button>
        ))}
      </div>
      <SignBlocks labels={["Operator sign-off", "Supervisor review"]} />
    </div>
  );
}

const BODIES = {
  "itp-template": ItpBody, "construction-variation-template": VariationBody, "progress-claim-template": ClaimBody,
  "pre-start-template": PreStartBody, "site-diary-template": SiteDiaryBody,
  "toolbox-talk-template": ToolboxBody, "plant-inspection-template": PlantInspectionBody,
};

export function TemplatePage({ slug }) {
  const t = TEMPLATES.find((x) => x.slug === slug);
  const Body = BODIES[slug];
  useEffect(() => { hubTrack("template_started", { tool: slug }); }, [slug]);
  const trail = [{ label: "Home", to: "/" }, { label: "Civil Hub", to: "/resources" }, { label: t.name }];
  const print = () => { hubTrack("template_downloaded", { tool: t.slug }); window.print(); };
  return (
    <div className="bg-[#f6f5f1] text-[#142635] min-h-screen">
      <SEO title={t.title} description={t.description} path={`/templates/${t.slug}`} jsonLd={breadcrumbJsonLd(trail)} />
      <div className="print:hidden"><MarketingHeader /><Breadcrumb trail={trail} /></div>
      <section className="max-w-5xl mx-auto px-6 md:px-12 py-10">
        <div className="block text-[11px] font-mono font-bold uppercase tracking-[0.28em] text-[#a63c0b] mb-3 print:hidden">Free template · No signup</div>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-display font-black uppercase tracking-tight text-3xl sm:text-4xl">{t.name}</h1>
            <p className="text-[#52616c] max-w-2xl mt-2 print:hidden">{t.intro}</p>
          </div>
          <button onClick={print} className="rounded-md bg-[#FF5F15] hover:bg-[#E65100] text-[#142635] text-[11px] font-black uppercase tracking-wider px-5 py-3 inline-flex items-center gap-2 print:hidden" data-testid="tpl-print">
            <Printer className="w-4 h-4" /> Print / save PDF
          </button>
        </div>
        <div className="mt-7"><Body /></div>
        <p className="text-[10.5px] text-[#52616c] mt-6">Indicative template only. Adapt to your contract, specification and site requirements before use.</p>
        <div className="mt-8 rounded-lg border border-[#cbd3d5] bg-white p-5 flex flex-wrap items-center justify-between gap-4 print:hidden">
          <p className="text-sm text-[#465562] max-w-xl">{t.cta.text}</p>
          <Link to={t.cta.to} onClick={() => hubTrack("fieldlink_cta_clicked", { tool: t.slug })}
            className="rounded-md bg-[#FF5F15] hover:bg-[#E65100] text-[#142635] text-xs font-bold uppercase tracking-widest px-5 py-2.5 inline-flex items-center gap-1.5">
            {t.cta.label} <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
        <div className="mt-6 print:hidden">
          <p className="text-[11px] font-bold uppercase tracking-widest text-[#52616c] mb-2">More free templates</p>
          <div className="flex flex-wrap gap-2">
            {TEMPLATES.filter((x) => x.slug !== slug).map((x) => (
              <Link key={x.slug} to={`/templates/${x.slug}`} className="text-xs font-bold uppercase tracking-wider rounded-md border border-[#cbd3d5] bg-white hover:border-[#FF5F15]/60 text-[#344451] px-3 py-1.5" data-testid={`tpl-related-${x.slug}`}>{x.name}</Link>
            ))}
            <Link to="/resources" className="text-xs font-bold uppercase tracking-wider rounded-md border border-[#cbd3d5] bg-white hover:border-[#FF5F15]/60 text-[#344451] px-3 py-1.5">All Civil Hub tools</Link>
          </div>
        </div>
      </section>
      <div className="print:hidden"><PublicFooter /></div>
    </div>
  );
}
